import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild, Input, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { DomSanitizer, SafeHtml, Title } from '@angular/platform-browser';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ControlchatV2Service } from '../../services/controlchat-v2.service';
import { API_BASE_URL } from '../../services/api-base';
import { ControlchatSocketService } from '../../services/controlchat-socket.service';
import { UserSessionService } from '../../services/user-session.service';
import { Subscription } from 'rxjs';

interface ChatUser {
  id: number;
  name: string;
  email?: string;
  online?: boolean;
  picture_user?: string | null;
  picture_url?: string | null;
  avatar?: string | null;
  photo?: string | null;
  role?: string | null;
}

interface ChatConversation {
  id: number;
  type: 'DIRECT' | 'GROUP' | 'AI';
  last_message_at?: string | null;
  last_activity_at?: string | null;
  last_message_text?: string | null;
  last_message_file_name?: string | null;
  last_message_file_type?: string | null;
  last_message_file_path?: string | null;
  last_message_audio_duration?: number | null;
  group_name?: string | null;
  group_created_by?: number | null;
  members_count?: number | null;
  group_only_admins?: number | null;
  other_user_id?: number | null;
  other_user_name?: string | null;
  other_user_email?: string | null;
  other_user_picture?: string | null;
  self_last_read_at?: string | null;
  other_last_read_at?: string | null;
  has_unread?: number | boolean | null;
  mentioned?: number | boolean | null;
  assistant_command_id?: number | null;
  assistant_title?: string | null;
  assistant_objective?: string | null;
  assistant_model?: string | null;
  assistant_version?: number | null;
  assistant_thread_id?: number | null;
  assistant_transaction_id?: number | null;
}

interface ChatMessageAttachment {
  id?: number | null;
  message_id?: number | null;
  file_name?: string | null;
  file_path?: string | null;
  file_type?: string | null;
  file_size?: number | null;
  created_at?: string | null;
}

interface ChatMessage {
  id: number;
  conversation_id: number;
  sender_id: number;
  sender_name?: string;
  message_text?: string | null;
  file_name?: string | null;
  file_path?: string | null;
  file_type?: string | null;
  file_size?: number | null;
  sender_picture?: string | null;
  reply_to_message_id?: number | null;
  reply_message_text?: string | null;
  reply_file_name?: string | null;
  reply_file_path?: string | null;
  reply_file_type?: string | null;
  reply_audio_duration?: number | null;
  reply_sender_name?: string | null;
  reactions?: Array<{ emoji: string; count: number; reacted?: boolean }>;
  audio_duration_seconds?: number | null;
  created_at: string;
  deleted_at?: string | null;
  read_count?: number | null;
  attachments?: ChatMessageAttachment[];
  payload?: any;
}

interface AssistantQuickReply {
  label: string;
  value: string;
  action: string;
}

interface ChatAssistant {
  id: number;
  command_id: number;
  title: string;
  objective?: string | null;
  model?: string | null;
  version?: number | null;
  thread_id?: number | null;
  current_transaction_id?: number | null;
  last_activity_at?: string | null;
  last_message_text?: string | null;
}

@Component({
  selector: 'app-controlchat-v2-widget',
  templateUrl: './controlchat-v2-widget.component.html',
  styleUrls: ['./controlchat-v2-widget.component.scss']
})
export class ControlchatV2WidgetComponent implements OnInit, OnDestroy {
  @Input() embedded = false;
  @ViewChild('messageInput') messageInput?: ElementRef<HTMLDivElement>;
  @ViewChild('messagesContainer') messagesContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('messageSearchInput') messageSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('groupForm') groupForm?: ElementRef<HTMLDivElement>;
  @ViewChild('monitoringSection') monitoringSection?: ElementRef<HTMLDivElement>;

  private readonly apiBase = API_BASE_URL;

  isOpen = false;
  loadingUsers = false;
  loadingConversations = false;
  loadingMessages = false;
  loadingAssistants = false;
  loadingKeywords = false;
  loadingMonitoring = false;

  searchTerm = '';
  users: ChatUser[] = [];
  conversations: ChatConversation[] = [];
  assistants: ChatAssistant[] = [];
  messages: ChatMessage[] = [];
  messagePageSize = 10;
  hasMoreMessages = true;
  loadingOlderMessages = false;
  oldestMessageId: number | null = null;
  messageSearchTerm = '';
  showMessageSearch = false;
  messageMatchIds: number[] = [];
  activeMatchIndex = 0;
  messageSearchLoading = false;
  selectedConversation: ChatConversation | null = null;
  selectedUser: ChatUser | null = null;
  messageText = '';
  replyToMessage: ChatMessage | null = null;
  pendingFiles: Array<{
    file: File;
    previewUrl: string | null;
    audioPreviewUrl: string | null;
    audioDuration: number | null;
  }> = [];
  imageModalSrc: string | null = null;
  capturingScreen = false;
  sendingAssistantMessage = false;
  readonly assistantLoadingMessages = [
    'Aguarde enquanto estou analisando...',
    'Já estou finalizando...',
    'Só mais um momento...'
  ];
  assistantLoadingMessage = this.assistantLoadingMessages[0];
  private lastQuickReplyClickAt = 0;
  private assistantLoadingTimerId: number | null = null;
  private assistantProcessingPollId: number | null = null;
  private assistantProcessingCommandId: number | null = null;
  isRecording = false;
  recordingSeconds = 0;
  private mediaRecorder: MediaRecorder | null = null;
  private recordingTimerId: number | null = null;
  private autoSendRecording = false;
  private recordingKeyHandler = (event: KeyboardEvent) => {
    if (!this.isRecording) return;
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.sendMessage();
  };
  isCreatingGroup = false;
  groupName = '';
  groupMemberQuery = '';
  groupMemberResults: ChatUser[] = [];
  groupMembers: ChatUser[] = [];
  isGroupPanelOpen = false;
  isGroupAddMode = false;
  groupMemberSearch = '';
  groupMemberSearchResults: ChatUser[] = [];
  groupMemberFilter = '';
  groupMembersList: ChatUser[] = [];
  unreadConversationIds = new Set<number>();
  lastIncomingConversationId: number | null = null;
  isHeaderMenuOpen = false;
  headerMenuView: 'root' | 'create-group' | 'keywords' = 'root';
  showGroupSettings = false;
  groupOnlyAdmins = false;
  draggingMessage = false;
  showEmojiPicker = false;
  reactionPickerMessageId: number | null = null;
  private editorSelection: Range | null = null;
  showMentionList = false;
  mentionQuery = '';
  private mentionStartIndex = -1;
  private mentionEndIndex = -1;
  private userNearBottom = true;
  reloadingDesktopLayout = false;
  private readonly audioWaveCache = new Map<string, number[]>();
  private readonly audioPlaybackState = new Map<string, { duration: number; current: number; playing: boolean }>();
  private activeAudioElement: HTMLAudioElement | null = null;
  private activeAudioKey: string | null = null;

  readonly emojiList = ['👍', '❤️', '😂', '😮', '😢', '🙏', '👏', '🔥', '✅', '🎉'];
  soundEnabled = true;
  private audioContext: AudioContext | null = null;
  private audioUnlocked = false;
  private unlockHandler = () => this.unlockAudioContext();
  private notificationAudio: HTMLAudioElement | null = null;

  canView = false;
  canCreate = false;
  canManage = false;
  isRestrictedProfile = false;

  keywords: Array<{ id: number; phrase: string; is_active: number }> = [];
  keywordInput = '';
  monitoringHits: any[] = [];

  private tenantId: number | null = null;
  private tenantName = '';
  userId: number | null = null;
  private userSub: Subscription | null = null;
  private socketReady = false;
  private activeConversationId: number | null = null;
  currentUser: ChatUser | null = null;

  constructor(
    private chatService: ControlchatV2Service,
    private socketService: ControlchatSocketService,
    private userSession: UserSessionService,
    private zone: NgZone,
    private sanitizer: DomSanitizer,
    private snackBar: MatSnackBar,
    private titleService: Title,
    private auth: AuthService,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadSoundPreference();
    this.attachAudioUnlockListeners();
    if (this.embedded) {
      this.isOpen = true;
      this.forceDesktopSilentMode();
      this.ensureNotificationPermission();
    }
    this.userSub = this.userSession.user$.subscribe((user) => {
      if (!user?.tenant_id || !user?.id) {
        this.stopAssistantProcessingPoll();
        this.isOpen = false;
        this.canView = false;
        this.canCreate = false;
        this.canManage = false;
        this.isRestrictedProfile = false;
        this.tenantId = null;
        this.userId = null;
        this.currentUser = null;
        this.users = [];
        this.conversations = [];
        this.assistants = [];
        this.messages = [];
        this.selectedConversation = null;
        this.selectedUser = null;
        this.socketService.disconnect();
        this.socketReady = false;
        this.tenantName = '';
        this.updateBrowserTitle();
        return;
      }
      const nextTenantId = Number(user.tenant_id);
      const nextUserId = Number(user.id);
      if (this.tenantId === nextTenantId && this.userId === nextUserId && this.socketReady) {
        return;
      }
      this.tenantId = nextTenantId;
      this.userId = nextUserId;
      const currentName = String(
        user?.['name'] || user?.['user_name'] || user?.['full_name'] || user?.['email'] || ''
      ).trim();
      const currentEmail = String(user?.['email'] || user?.['user_email'] || '').trim();
      const currentAvatar =
        user?.['picture_user'] || user?.['picture_url'] || user?.['avatar'] || user?.['photo'] || null;
      this.currentUser = {
        id: nextUserId,
        name: currentName || currentEmail || 'Usuário',
        email: currentEmail || undefined,
        picture_user: currentAvatar
      };
      this.tenantName = String(user?.tenant_name ?? user?.tenant?.name ?? '').trim();
      const roleName = String(user?.role_name || user?.role?.name || '').toLowerCase();
      const isMaster = Number(user?.['is_master'] || user?.['isMaster'] || 0) === 1;
      const isAdmin = roleName.includes('admin') || roleName.includes('master') || Number(user?.role_id || 0) === 1 || isMaster;
      this.isRestrictedProfile = roleName.includes('analisa') || roleName.includes('analista') || roleName.includes('analyst');
      this.syncPermissions(user['modules'] || [], isAdmin);
      this.connectSocket();
      this.loadConversations();
      if (this.isOpen) {
        this.bootstrapChat();
      }
      this.updateBrowserTitle();
    });
  }

  ngOnDestroy() {
    this.stopAssistantLoadingOverlay();
    this.stopAssistantProcessingPoll();
    this.userSub?.unsubscribe();
    this.socketService.disconnect();
    this.detachAudioUnlockListeners();
    this.clearAllPendingFiles();
    this.stopActiveAudioPlayback();
    this.audioPlaybackState.clear();
    this.audioWaveCache.clear();
  }

  toggleOpen() {
    if (this.embedded) {
      return;
    }
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.bootstrapChat();
      this.unlockAudioContext();
      if (this.selectedConversation?.id) {
        this.userNearBottom = true;
        this.scrollMessagesToBottom(true);
        this.markConversationReadIfActive();
      }
      return;
    }
  }

  closePanel() {
    if (this.embedded) {
      return;
    }
    this.stopActiveAudioPlayback();
    this.isOpen = false;
    this.isHeaderMenuOpen = false;
    this.headerMenuView = 'root';
    this.showMessageSearch = false;
    this.showEmojiPicker = false;
    this.reactionPickerMessageId = null;
  }

  logoutDesktop() {
    this.auth.logout();
    localStorage.removeItem('controlchat_desktop_last_login');
    localStorage.removeItem('controlchat_desktop_mode');
    this.router.navigate(['/login'], { queryParams: { reason: 'logout', desktop: '1' } });
    setTimeout(() => window.close(), 200);
  }

  refreshDesktopLayout() {
    if (!this.embedded || this.reloadingDesktopLayout) {
      return;
    }
    this.reloadingDesktopLayout = true;
    window.location.reload();
  }

  getUserAvatarUrl(user: ChatUser | null) {
    if (!user) return null;
    const raw = user.picture_user || user.picture_url || user.avatar || user.photo || null;
    return this.getAvatarUrl(raw);
  }

  getConversationAvatarUrl(conv: ChatConversation | null) {
    if (!conv) return null;
    const raw = conv.other_user_picture || null;
    return this.getAvatarUrl(raw);
  }

  getMessageAvatarUrl(message: ChatMessage | null) {
    if (!message) return null;
    return this.getAvatarUrl(message.sender_picture || null);
  }

  private getAvatarUrl(raw?: string | null) {
    if (!raw) return null;
    if (raw.startsWith('http')) {
      return raw;
    }
    if (!raw.startsWith('/')) {
      return `${this.apiBase}/${raw}`;
    }
    return `${this.apiBase}${raw}`;
  }

  private syncPermissions(modules: any[], isAdmin = false) {
    if (isAdmin) {
      this.canView = true;
      this.canCreate = true;
      this.canManage = true;
      return;
    }
    if (!Array.isArray(modules) || !modules.length) {
      this.canView = true;
      this.canCreate = true;
      this.canManage = true;
      return;
    }
    const module = modules.find((item) => item.module_key === 'controlchat');
    this.canView = !!module?.can_view;
    this.canCreate = !!module?.can_create;
    this.canManage = !!module?.can_manage;
  }

  private connectSocket() {
    if (!this.tenantId || !this.userId) {
      return;
    }
    if (this.socketReady) {
      return;
    }
    const socket = this.socketService.connect(this.tenantId, this.userId);
    this.socketReady = true;
    this.socketService.on('presence:sync', (payload: any) => {
      this.zone.run(() => {
        const ids = new Set<number>(payload?.onlineUserIds || []);
        this.users = this.users.map((user) => ({ ...user, online: ids.has(user.id) }));
      });
    });
    this.socketService.on('presence:update', (payload: any) => {
      this.zone.run(() => {
        if (!payload?.userId) {
          return;
        }
        const targetId = Number(payload.userId);
        this.users = this.users.map((user) =>
          user.id === targetId ? { ...user, online: !!payload.online } : user
        );
      });
    });
    this.socketService.on('message:new', (message: ChatMessage) => {
      this.zone.run(() => {
        if (!message || !this.userId) {
          return;
        }
        this.updateConversationOnMessage(message);
        const isOwnMessage = message.sender_id === this.userId;
        const isViewingConversation =
          this.isOpen && this.selectedConversation?.id === message.conversation_id;
        if (isViewingConversation) {
          this.addMessageUnique(message);
          this.scrollMessagesToBottom();
          this.clearConversationUnread(message.conversation_id);
          if (!isOwnMessage) {
            this.markConversationReadIfActive();
          }
          return;
        }
        if (!isOwnMessage) {
          this.markConversationUnread(message.conversation_id);
          if (this.soundEnabled) {
            this.playNotificationSound();
          }
          this.showDesktopNotification(message);
        }
      });
    });
    this.socketService.on('conversation:read', (payload: any) => {
      this.zone.run(() => {
        const conversationId = Number(payload?.conversation_id || 0);
        const readerId = Number(payload?.user_id || 0);
        if (!conversationId || !readerId || readerId === this.userId) {
          return;
        }
        // Avoid inflating read counters on repeated read events.
      });
    });
    this.socketService.on('reaction:update', (payload: any) => {
      this.zone.run(() => {
        const messageId = Number(payload?.message_id || 0);
        if (!messageId) return;
        const reactions = payload?.reactions || [];
        this.messages = this.messages.map((msg) =>
          msg.id === messageId ? { ...msg, reactions } : msg
        );
      });
    });
    this.socketService.on('message:update', (payload: any) => {
      this.zone.run(() => {
        const messageId = Number(payload?.id || 0);
        if (!messageId) return;
        if (payload?.deleted_at) {
          this.messages = this.messages.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  message_text: null,
                  file_name: null,
                  file_path: null,
                  file_type: null,
                  file_size: null,
                  audio_duration_seconds: null,
                  deleted_at: payload.deleted_at
                }
              : msg
          );
        }
      });
    });
  }

  private bootstrapChat() {
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.loadUsers();
    this.loadAssistants();
    this.loadConversations();
    if (this.canManage) {
      this.loadKeywords();
      this.loadMonitoring();
    }
  }

  private ensureNotificationPermission() {
    if (!this.embedded || typeof Notification === 'undefined') {
      return;
    }
    if (Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }

  private showDesktopNotification(message: ChatMessage) {
    if (!this.embedded || typeof Notification === 'undefined') {
      return;
    }
    if (Notification.permission !== 'granted') {
      return;
    }
    const senderName = String(message?.sender_name || 'Nova mensagem');
    const body = this.getNotificationBody(message);
    const notification = new Notification(senderName, {
      body,
      icon: '/assets/images/controlchat_desktop/icone.png',
      silent: !this.soundEnabled
    });
    notification.onclick = () => {
      window.focus();
      const target = this.conversations.find((conv) => conv.id === message.conversation_id);
      if (target) {
        this.zone.run(() => this.openConversation(target));
      }
      notification.close();
    };
    notification.onshow = () => {
      window.setTimeout(() => notification.close(), 6000);
    };
  }

  private getNotificationBody(message: ChatMessage) {
    if (message?.file_path) {
      if (this.isAudioUrl(message.file_path)) {
        const duration = Number(message.audio_duration_seconds || 0);
        return duration ? `Áudio · ${this.formatRecordingTime(duration)}` : 'Áudio';
      }
      if (this.isImageUrl(message.file_path)) {
        return 'Imagem';
      }
      const ext = this.extractFileExt(message.file_name || message.file_path);
      return ext ? `Arquivo .${ext}` : 'Arquivo';
    }
    const raw = String(message?.message_text || '').trim();
    if (!raw) {
      return 'Você recebeu uma nova mensagem.';
    }
    const clean = this.stripHtml(raw);
    return clean || 'Você recebeu uma nova mensagem.';
  }

  private stripHtml(value: string): string {
    try {
      const doc = new DOMParser().parseFromString(value, 'text/html');
      const text = doc.body?.textContent || '';
      return text.replace(/\s+/g, ' ').trim();
    } catch (_) {
      return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    }
  }

  loadUsers() {
    if (!this.tenantId || !this.userId || !this.canView) return;
    this.loadingUsers = true;
    this.chatService.getUsers(this.tenantId, this.userId, this.searchTerm).subscribe({
      next: (rows) => {
        this.users = rows || [];
        this.loadingUsers = false;
      },
      error: () => {
        this.loadingUsers = false;
        this.users = [];
      }
    });
  }

  private assistantToConversation(assistant: ChatAssistant): ChatConversation {
    const commandId = Number(assistant.command_id || assistant.id);
    return {
      id: -Math.abs(commandId),
      type: 'AI',
      last_activity_at: assistant.last_activity_at || null,
      last_message_at: assistant.last_activity_at || null,
      last_message_text: assistant.last_message_text || null,
      other_user_name: assistant.title,
      other_user_email: assistant.model || null,
      assistant_command_id: commandId,
      assistant_title: assistant.title,
      assistant_objective: assistant.objective || null,
      assistant_model: assistant.model || null,
      assistant_version: Number(assistant.version || 1) || 1,
      assistant_thread_id: Number(assistant.thread_id || 0) || null,
      assistant_transaction_id: Number(assistant.current_transaction_id || 0) || null
    };
  }

  loadAssistants() {
    if (!this.tenantId || !this.userId || (!this.canView && !this.embedded)) return;
    this.loadingAssistants = true;
    this.chatService.getAiAssistants(this.tenantId, this.userId).subscribe({
      next: (rows) => {
        this.assistants = (rows || []).map((item: any) => ({
          id: Number(item?.id || item?.command_id || 0),
          command_id: Number(item?.command_id || item?.id || 0),
          title: String(item?.title || item?.name || `Assistente #${item?.command_id || item?.id || '-'}`),
          objective: item?.objective || null,
          model: item?.model || null,
          version: Number(item?.version || 1) || 1,
          thread_id: Number(item?.thread_id || 0) || null,
          current_transaction_id: Number(item?.current_transaction_id || 0) || null,
          last_activity_at: item?.last_activity_at || null,
          last_message_text: item?.last_message_text || null
        }))
        .sort((a, b) => {
          const timeA = a.last_activity_at ? new Date(a.last_activity_at).getTime() : 0;
          const timeB = b.last_activity_at ? new Date(b.last_activity_at).getTime() : 0;
          if (timeA !== timeB) {
            return timeB - timeA;
          }
          return String(a.title || '').localeCompare(String(b.title || ''), 'pt-BR');
        });
        if (this.selectedConversation?.type === 'AI') {
          const selectedCommandId = Number(this.selectedConversation.assistant_command_id || 0);
          const selected = this.assistants.find((assistant) => Number(assistant.command_id) === selectedCommandId);
          if (selected) {
            this.selectedConversation = this.assistantToConversation(selected);
          }
        }
        this.loadingAssistants = false;
      },
      error: () => {
        this.assistants = [];
        this.loadingAssistants = false;
      }
    });
  }

  openAssistantConversation(assistant: ChatAssistant) {
    const conversation = this.assistantToConversation(assistant);
    this.openConversation(conversation);
  }

  isAssistantSelected(assistant: ChatAssistant) {
    return (
      this.selectedConversation?.type === 'AI' &&
      Number(this.selectedConversation.assistant_command_id || 0) === Number(assistant.command_id)
    );
  }

  getAssistantPreview(assistant: ChatAssistant) {
    const text = this.extractPlainText(String(assistant?.last_message_text || '')).trim();
    if (!text) {
      return 'Assistente virtual do ControlAI';
    }
    return this.truncatePreview(text);
  }

  loadConversations() {
    if (!this.tenantId || !this.userId) return;
    this.loadingConversations = true;
    this.chatService.getConversations(this.tenantId, this.userId).subscribe({
      next: (rows) => {
        const normalized = (rows || []).map((conv) => ({
          ...conv,
          mentioned: conv.mentioned ?? (this.isMentionForUser(String(conv.last_message_text || '')) ? 1 : 0)
        }));
        this.conversations = normalized.filter((conv) => {
          if (conv.type === 'GROUP') return true;
          const hasMessage = !!(conv.last_message_at || conv.last_message_text || conv.last_message_file_path);
          return hasMessage;
        });
        if (this.selectedConversation?.id) {
          const updated = this.conversations.find((item) => item.id === this.selectedConversation?.id);
          if (updated) {
            this.selectedConversation = updated;
          }
        }
        this.syncUnreadConversations();
        const ids = this.conversations.map((conv) => conv.id);
        this.socketService.joinConversations(ids);
        this.loadingConversations = false;
      },
      error: () => {
        this.loadingConversations = false;
      }
    });
  }

  selectUser(user: ChatUser) {
    if (!this.tenantId || !this.userId) return;
    this.selectedUser = user;
    this.selectedConversation = null;
    this.messages = [];
    this.messageText = '';
    this.setEditorHtml('');
    this.clearAllPendingFiles();
    this.chatService.createDirectConversation(this.tenantId, this.userId, user.id).subscribe({
      next: (response) => {
        const convId = Number(response?.conversation_id || 0);
        if (convId) {
          this.openConversation({
            id: convId,
            type: 'DIRECT',
            other_user_id: user.id,
            other_user_name: user.name,
            other_user_email: user.email,
            other_user_picture: user.picture_user || user.picture_url || user.avatar || user.photo || null
          });
          this.loadConversations();
        }
      }
    });
  }

  clearSearch() {
    this.searchTerm = '';
    this.loadUsers();
  }

  openConversation(conv: ChatConversation) {
    this.stopActiveAudioPlayback();
    if (conv.type !== 'AI') {
      this.stopAssistantProcessingPoll();
    }
    this.selectedConversation = conv;
    this.activeConversationId = conv.id;
    this.userNearBottom = true;
    this.showGroupSettings = false;
    this.groupOnlyAdmins = !!conv.group_only_admins;
    this.replyToMessage = null;
    this.reactionPickerMessageId = null;
    this.messageText = '';
    this.setEditorHtml('');
    this.clearAllPendingFiles();
    this.messages = [];
    this.hasMoreMessages = true;
    this.loadingOlderMessages = false;
    this.oldestMessageId = null;
    this.clearConversationUnread(conv.id);
    this.selectedUser = null;
    this.isGroupPanelOpen = false;
    this.isGroupAddMode = false;
    this.groupMemberSearch = '';
    this.groupMemberSearchResults = [];
    this.groupMemberFilter = '';
    this.groupMembersList = [];
    this.messageSearchTerm = '';
    this.showMessageSearch = false;
    this.updateMessageMatches();
    this.loadMessages();
    if (conv.type === 'GROUP') {
      this.loadGroupMembers();
    }
    this.focusMessageInput();
  }

  markConversationReadIfActive() {
    if (!this.isOpen || !this.selectedConversation?.id || this.selectedConversation.type === 'AI') return;
    this.markConversationRead(this.selectedConversation.id);
  }

  loadMessages() {
    if (!this.tenantId || !this.userId || !this.selectedConversation) return;
    if (this.selectedConversation.type === 'AI') {
      const commandId = Number(this.selectedConversation.assistant_command_id || 0);
      if (!commandId) {
        this.messages = [];
        this.loadingMessages = false;
        return;
      }
      this.activeConversationId = this.selectedConversation.id;
      this.loadingMessages = true;
      this.chatService.getAiAssistantMessages(this.tenantId, this.userId, commandId).subscribe({
        next: (payload) => {
          if (!this.selectedConversation || this.selectedConversation.type !== 'AI') {
            return;
          }
          const rows = Array.isArray(payload?.messages) ? payload.messages : [];
          const mapped = rows.map((msg: any) => this.normalizeMessage(msg));
          this.messages = this.uniqueMessages(mapped);
          this.hasMoreMessages = false;
          this.oldestMessageId = this.messages.length ? this.messages[0].id : null;
          this.loadingMessages = false;
          this.updateMessageMatches();
          this.scrollMessagesToBottom();
        },
        error: () => {
          if (this.selectedConversation?.type === 'AI') {
            this.loadingMessages = false;
            this.messages = [];
          }
        }
      });
      return;
    }

    const conversationId = this.selectedConversation.id;
    this.activeConversationId = conversationId;
    this.loadingMessages = true;
    this.chatService.getMessages(this.tenantId, this.userId, conversationId, this.messagePageSize).subscribe({
      next: (payload) => {
        if (!this.selectedConversation || this.selectedConversation.id !== conversationId || this.activeConversationId !== conversationId) {
          return;
        }
        const rows = payload?.messages || [];
        const filtered = rows
          .filter((msg) => Number(msg?.conversation_id) === conversationId)
          .map((msg) => this.normalizeMessage(msg));
        this.messages = this.uniqueMessages(filtered);
        this.hasMoreMessages = !!payload?.has_more;
        this.oldestMessageId = this.messages.length ? this.messages[0].id : null;
        this.loadingMessages = false;
        this.updateMessageMatches();
        this.scrollMessagesToBottom();
        this.markConversationReadIfActive();
      },
      error: () => {
        if (this.selectedConversation && this.selectedConversation.id === conversationId) {
          this.loadingMessages = false;
        }
      }
    });
  }

  loadOlderMessages() {
    if (!this.tenantId || !this.userId || !this.selectedConversation) return;
    if (this.selectedConversation.type === 'AI') return;
    if (!this.hasMoreMessages || this.loadingOlderMessages) return;
    if (!this.oldestMessageId) return;
    const conversationId = this.selectedConversation.id;
    const container = this.messagesContainer?.nativeElement;
    const previousHeight = container?.scrollHeight || 0;
    this.loadingOlderMessages = true;
    this.chatService
      .getMessages(this.tenantId, this.userId, conversationId, this.messagePageSize, this.oldestMessageId)
      .subscribe({
        next: (payload) => {
          if (!this.selectedConversation || this.selectedConversation.id !== conversationId) {
            this.loadingOlderMessages = false;
            return;
          }
          const rows = payload?.messages || [];
          const filtered = rows
            .filter((msg) => Number(msg?.conversation_id) === conversationId)
            .map((msg) => this.normalizeMessage(msg));
          if (filtered.length) {
            this.messages = this.uniqueMessages([...filtered, ...this.messages]);
            this.oldestMessageId = this.messages[0]?.id || this.oldestMessageId;
          }
          this.hasMoreMessages = !!payload?.has_more;
          this.loadingOlderMessages = false;
          this.updateMessageMatches();
          setTimeout(() => {
            const el = this.messagesContainer?.nativeElement;
            if (el) {
              const newHeight = el.scrollHeight;
              el.scrollTop = newHeight - previousHeight;
            }
          }, 0);
        },
        error: () => {
          this.loadingOlderMessages = false;
        }
      });
  }

  onSearchChange() {
    this.loadUsers();
  }

  toggleGroupPanel() {
    if (!this.selectedConversation || this.selectedConversation.type !== 'GROUP') return;
    this.isGroupPanelOpen = !this.isGroupPanelOpen;
    if (!this.isGroupPanelOpen) {
      this.isGroupAddMode = false;
      this.groupMemberSearch = '';
      this.groupMemberSearchResults = [];
      this.groupMemberFilter = '';
    } else {
      this.loadGroupMembers();
    }
  }

  toggleGroupAddMode() {
    if (!this.selectedConversation || this.selectedConversation.type !== 'GROUP') return;
    this.isGroupPanelOpen = true;
    this.isGroupAddMode = !this.isGroupAddMode;
    if (this.isGroupAddMode) {
      this.groupMemberFilter = '';
      this.groupMemberSearch = '';
      this.groupMemberSearchResults = [];
    } else {
      this.groupMemberSearch = '';
      this.groupMemberSearchResults = [];
      this.groupMemberFilter = '';
    }
  }

  onGroupMemberFilterChange() {
    if (!this.isGroupAddMode) {
      return;
    }
    this.groupMemberSearch = this.groupMemberFilter;
    this.onGroupMemberSearchChange();
  }

  private onGroupMemberSearchChange() {
    if (!this.tenantId || !this.userId || !this.selectedConversation) return;
    const query = this.groupMemberSearch.trim();
    if (!query) {
      this.groupMemberSearchResults = [];
      return;
    }
    this.chatService.getUsers(this.tenantId, this.userId, query).subscribe({
      next: (rows) => {
        const results = rows || [];
        const existingIds = new Set(this.groupMembersList.map((member) => member.id));
        this.groupMemberSearchResults = results.filter((user) => user.id !== this.userId && !existingIds.has(user.id));
      },
      error: () => {
        this.groupMemberSearchResults = [];
      }
    });
  }

  addMemberToSelectedGroup(user: ChatUser) {
    if (!this.tenantId || !this.userId || !this.selectedConversation || this.selectedConversation.type !== 'GROUP') {
      return;
    }
    this.chatService.addGroupMember(this.tenantId, this.userId, this.selectedConversation.id, user.id).subscribe({
      next: () => {
        this.groupMemberSearch = '';
        this.groupMemberSearchResults = [];
        this.loadGroupMembers();
        this.loadConversations();
        this.snackBar.open('Integrante adicionado ao grupo.', 'OK', { duration: 2500 });
      }
    });
  }

  canDeleteSelectedGroup() {
    return this.selectedConversation?.type === 'GROUP' && !!this.userId && this.selectedConversation.group_created_by === this.userId;
  }

  deleteSelectedGroup() {
    if (!this.tenantId || !this.userId || !this.selectedConversation || this.selectedConversation.type !== 'GROUP') {
      return;
    }
    const confirmed = window.confirm('Deseja excluir este grupo?');
    if (!confirmed) return;
    this.chatService.deleteGroup(this.tenantId, this.userId, this.selectedConversation.id).subscribe({
      next: () => {
        this.selectedConversation = null;
        this.messages = [];
        this.isGroupPanelOpen = false;
        this.isGroupAddMode = false;
        this.loadConversations();
      }
    });
  }

  private loadGroupMembers() {
    if (!this.tenantId || !this.userId || !this.selectedConversation || this.selectedConversation.type !== 'GROUP') {
      return;
    }
    this.chatService.getGroupMembers(this.tenantId, this.userId, this.selectedConversation.id).subscribe({
      next: (rows) => {
        this.groupMembersList = rows || [];
      },
      error: () => {
        this.groupMembersList = [];
      }
    });
  }

  get filteredGroupMembers() {
    const term = this.groupMemberFilter.trim().toLowerCase();
    if (!term) return this.groupMembersList;
    return this.groupMembersList.filter((member) => {
      const name = String(member.name || '').toLowerCase();
      const email = String(member.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }

  isGroupOwner() {
    return (
      this.selectedConversation?.type === 'GROUP' &&
      !!this.userId &&
      this.selectedConversation.group_created_by === this.userId
    );
  }

  private getCurrentGroupRole() {
    if (!this.userId) return null;
    const member = this.groupMembersList.find((item) => item.id === this.userId);
    return String(member?.role || '').toUpperCase() || null;
  }

  isGroupAdminOrOwner() {
    if (this.isGroupOwner()) return true;
    const role = this.getCurrentGroupRole();
    return role === 'ADMIN' || role === 'OWNER';
  }

  get isMessageBlocked() {
    return (
      this.selectedConversation?.type === 'GROUP' &&
      this.groupOnlyAdmins &&
      !this.isGroupAdminOrOwner()
    );
  }

  canToggleMemberRole(member: ChatUser) {
    if (!this.isGroupAdminOrOwner()) return false;
    if (!member || !member.id) return false;
    return member.id !== this.userId && member.role !== 'OWNER';
  }

  canRemoveMember(member: ChatUser) {
    if (!this.isGroupAdminOrOwner()) return false;
    if (!member || !member.id) return false;
    return member.id !== this.userId && member.role !== 'OWNER';
  }

  toggleMemberAdmin(member: ChatUser) {
    if (!this.tenantId || !this.userId || !this.selectedConversation || this.selectedConversation.type !== 'GROUP') {
      return;
    }
    if (!this.canToggleMemberRole(member)) {
      return;
    }
    const nextRole = member.role === 'ADMIN' ? 'MEMBER' : 'ADMIN';
    this.chatService
      .updateGroupMemberRole(this.tenantId, this.userId, this.selectedConversation.id, member.id, nextRole)
      .subscribe({
        next: () => {
          this.groupMembersList = this.groupMembersList.map((item) =>
            item.id === member.id ? { ...item, role: nextRole } : item
          );
          this.snackBar.open(
            nextRole === 'ADMIN' ? 'Integrante promovido a admin.' : 'Admin removido.',
            'OK',
            { duration: 2500 }
          );
        }
      });
  }

  removeGroupMember(member: ChatUser) {
    if (!this.tenantId || !this.userId || !this.selectedConversation || this.selectedConversation.type !== 'GROUP') {
      return;
    }
    if (!this.isGroupAdminOrOwner()) {
      return;
    }
    if (member.id === this.userId) {
      return;
    }
    const confirmed = window.confirm('Deseja remover este integrante do grupo?');
    if (!confirmed) return;
    this.chatService
      .removeGroupMember(this.tenantId, this.userId, this.selectedConversation.id, member.id)
      .subscribe({
        next: () => {
          this.groupMembersList = this.groupMembersList.filter((item) => item.id !== member.id);
          this.loadConversations();
          this.snackBar.open('Integrante removido do grupo.', 'OK', { duration: 2500 });
        }
      });
  }

  isMemberAdmin(member: ChatUser) {
    const role = String(member?.role || '').toUpperCase();
    return role === 'ADMIN' || role === 'OWNER';
  }

  toggleGroupForm() {
    if (!this.canCreate) return;
    this.isCreatingGroup = !this.isCreatingGroup;
    if (!this.isCreatingGroup) {
      this.resetGroupForm();
    }
  }

  onGroupMemberSearch() {
    if (!this.tenantId || !this.userId) return;
    const query = this.groupMemberQuery.trim();
    if (!query) {
      this.groupMemberResults = [];
      return;
    }
    this.chatService.getUsers(this.tenantId, this.userId, query).subscribe({
      next: (rows) => {
        const results = rows || [];
        const selectedIds = new Set(this.groupMembers.map((member) => member.id));
        this.groupMemberResults = results.filter((user) => !selectedIds.has(user.id));
      },
      error: () => {
        this.groupMemberResults = [];
      }
    });
  }

  addGroupMember(user: ChatUser) {
    const exists = this.groupMembers.some((member) => member.id === user.id);
    if (exists) return;
    this.groupMembers = [...this.groupMembers, user];
    this.groupMemberQuery = '';
    this.groupMemberResults = [];
  }

  removeDraftGroupMember(member: ChatUser) {
    this.groupMembers = this.groupMembers.filter((item) => item.id !== member.id);
  }

  toggleEmojiPicker() {
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  addEmojiToMessage(emoji: string) {
    if (!this.messageInput?.nativeElement) {
      this.messageText = `${this.messageText || ''}${emoji}`;
      this.showEmojiPicker = false;
      return;
    }
    this.insertTextAtCursor(emoji);
    this.showEmojiPicker = false;
  }

  closeEmojiPicker() {
    this.showEmojiPicker = false;
  }

  preserveEditorSelection(event: Event) {
    this.saveEditorSelection();
  }

  saveEditorSelection() {
    const editor = this.messageInput?.nativeElement;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return;
    this.editorSelection = range;
  }

  private restoreEditorSelection() {
    const selection = window.getSelection();
    if (!selection || !this.editorSelection) return;
    selection.removeAllRanges();
    selection.addRange(this.editorSelection);
  }

  onEditorInput() {
    const html = this.getEditorHtml();
    this.messageText = html;
    if (this.isEditorEmpty(html)) {
      this.setEditorHtml('');
    }
    this.saveEditorSelection();
    this.updateMentionState();
  }

  applyFormat(command: string) {
    this.restoreEditorSelection();
    document.execCommand(command, false);
    this.onEditorInput();
  }

  applyColor(command: 'foreColor' | 'hiliteColor', event: Event) {
    const input = event.target as HTMLInputElement | null;
    if (!input?.value) return;
    this.restoreEditorSelection();
    document.execCommand(command, false, input.value);
    this.onEditorInput();
  }

  private insertTextAtCursor(text: string) {
    const editor = this.messageInput?.nativeElement;
    if (!editor) return;
    editor.focus();
    this.restoreEditorSelection();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      editor.appendChild(document.createTextNode(text));
      this.onEditorInput();
      return;
    }
    const range = selection.getRangeAt(0);
    range.deleteContents();
    range.insertNode(document.createTextNode(text));
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
    this.saveEditorSelection();
    this.onEditorInput();
  }

  onMessagePaste(event: ClipboardEvent) {
    const clipboard = event.clipboardData;
    if (!clipboard) {
      return;
    }
    const imageFiles: File[] = [];
    const items = Array.from(clipboard.items || []);
    items.forEach((item) => {
      if (item.kind !== 'file') return;
      if (!String(item.type || '').toLowerCase().startsWith('image/')) return;
      const raw = item.getAsFile();
      if (!raw) return;
      const ext = this.resolveImageExtension(raw.type);
      const normalized = new File([raw], `print-controlchat-${Date.now()}.${ext}`, {
        type: raw.type || 'image/png'
      });
      imageFiles.push(normalized);
    });
    if (!imageFiles.length) {
      return;
    }
    event.preventDefault();
    this.addPendingFiles(imageFiles);
    this.snackBar.open(
      imageFiles.length > 1 ? `${imageFiles.length} imagens coladas no anexo.` : 'Imagem colada no anexo.',
      'OK',
      { duration: 2200 }
    );
  }

  private resolveImageExtension(mimeType?: string | null) {
    const mime = String(mimeType || '').toLowerCase();
    if (mime.includes('jpeg') || mime.includes('jpg')) return 'jpg';
    if (mime.includes('webp')) return 'webp';
    if (mime.includes('gif')) return 'gif';
    if (mime.includes('bmp')) return 'bmp';
    if (mime.includes('svg')) return 'svg';
    return 'png';
  }

  private getEditorHtml() {
    return this.messageInput?.nativeElement?.innerHTML || this.messageText || '';
  }

  private setEditorHtml(html: string) {
    const editor = this.messageInput?.nativeElement;
    if (editor) {
      editor.innerHTML = html;
    }
    this.messageText = html;
  }

  private isEditorEmpty(html: string) {
    const text = this.extractPlainText(html);
    return !text.trim();
  }

  private placeCaretAtEnd(element: HTMLElement) {
    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(false);
    const selection = window.getSelection();
    if (!selection) return;
    selection.removeAllRanges();
    selection.addRange(range);
  }

  setReplyTo(message: ChatMessage) {
    this.replyToMessage = message;
    this.focusMessageInput();
  }

  clearReply() {
    this.replyToMessage = null;
  }

  @HostListener('document:keydown', ['$event'])
  handleCaptureShortcut(event: KeyboardEvent) {
    const key = String(event.key || '').toLowerCase();
    const isCaptureShortcut = (event.metaKey || event.ctrlKey) && event.shiftKey && key === 'd';
    if (!isCaptureShortcut) return;
    if ((!this.isOpen && !this.embedded) || !this.selectedConversation) return;
    event.preventDefault();
    event.stopPropagation();
    this.captureScreenAttachment();
  }

  toggleReactionPicker(message: ChatMessage) {
    if (!message?.id) return;
    this.reactionPickerMessageId = this.reactionPickerMessageId === message.id ? null : message.id;
  }

  addReaction(message: ChatMessage, emoji: string) {
    if (!this.tenantId || !this.userId || !message?.id) return;
    this.chatService.toggleReaction(this.tenantId, this.userId, message.id, emoji).subscribe({
      next: (payload) => {
        const nextReactions = payload?.reactions || [];
        this.messages = this.messages.map((msg) => (msg.id === message.id ? { ...msg, reactions: nextReactions } : msg));
        this.reactionPickerMessageId = null;
      }
    });
  }

  canDeleteMessage(message: ChatMessage) {
    if (!message || !this.userId) return false;
    return message.sender_id === this.userId;
  }

  deleteMessage(message: ChatMessage) {
    if (!this.tenantId || !this.userId || !message?.id) return;
    this.chatService.deleteMessage(this.tenantId, this.userId, message.id).subscribe({
      next: (payload) => {
        const deletedAt = payload?.deleted_at || null;
        this.messages = this.messages.map((msg) =>
          msg.id === message.id
            ? {
                ...msg,
                message_text: null,
                file_name: null,
                file_path: null,
                file_type: null,
                file_size: null,
                audio_duration_seconds: null,
                deleted_at: deletedAt || new Date().toISOString()
              }
            : msg
        );
      }
    });
  }

  trackMessage(index: number, msg: ChatMessage) {
    return msg.id;
  }

  onMessagesScroll() {
    const el = this.messagesContainer?.nativeElement;
    if (!el) return;
    const threshold = 80;
    this.userNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
    if (el.scrollTop <= 40) {
      this.loadOlderMessages();
    }
  }

  handleMessageKeydown(event: KeyboardEvent) {
    if (this.showMentionList && event.key === 'Escape') {
      event.preventDefault();
      this.closeMentionList();
      return;
    }
    if (event.key !== 'Enter') return;
    if (event.shiftKey) return;
    event.preventDefault();
    this.sendMessage();
  }

  get filteredMentionMembers() {
    if (!this.selectedConversation || this.selectedConversation.type !== 'GROUP') {
      return [];
    }
    const term = this.mentionQuery.trim().toLowerCase();
    return this.groupMembersList
      .filter((member) => member.id !== this.userId)
      .filter((member) => {
        if (!term) return true;
        const name = String(member.name || '').toLowerCase();
        const email = String(member.email || '').toLowerCase();
        return name.includes(term) || email.includes(term);
      })
      .slice(0, 8);
  }

  selectMentionMember(member: ChatUser) {
    if (!member) return;
    this.insertMentionToken(`@${this.formatUserName(member.name)}`, { id: member.id });
  }

  selectMentionAll() {
    this.insertMentionToken('@Todos', { all: true });
  }

  private updateMentionState() {
    if (!this.selectedConversation || this.selectedConversation.type !== 'GROUP') {
      this.closeMentionList();
      return;
    }
    const editor = this.messageInput?.nativeElement;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) {
      this.closeMentionList();
      return;
    }
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.endContainer)) {
      this.closeMentionList();
      return;
    }
    const preRange = range.cloneRange();
    preRange.selectNodeContents(editor);
    preRange.setEnd(range.endContainer, range.endOffset);
    const text = preRange.toString();
    const atIndex = text.lastIndexOf('@');
    if (atIndex === -1) {
      this.closeMentionList();
      return;
    }
    if (atIndex > 0 && !/\s/.test(text[atIndex - 1])) {
      this.closeMentionList();
      return;
    }
    const query = text.slice(atIndex + 1);
    if (!/^[^\s@]*$/.test(query)) {
      this.closeMentionList();
      return;
    }
    this.mentionQuery = query;
    this.mentionStartIndex = atIndex;
    this.mentionEndIndex = text.length;
    this.showMentionList = true;
  }

  private closeMentionList() {
    this.showMentionList = false;
    this.mentionQuery = '';
    this.mentionStartIndex = -1;
    this.mentionEndIndex = -1;
  }

  private insertMentionToken(label: string, options: { id?: number; all?: boolean }) {
    const editor = this.messageInput?.nativeElement;
    if (!editor || this.mentionStartIndex < 0 || this.mentionEndIndex < 0) {
      return;
    }
    editor.focus();
    const range = this.buildRangeFromTextIndexes(editor, this.mentionStartIndex, this.mentionEndIndex);
    if (!range) {
      return;
    }
    range.deleteContents();
    const span = document.createElement('span');
    span.className = 'mention-tagged';
    span.textContent = label;
    if (options.all) {
      span.setAttribute('data-mention', 'all');
    }
    if (options.id) {
      span.setAttribute('data-mention-id', String(options.id));
    }
    span.setAttribute('contenteditable', 'false');
    const space = document.createTextNode(' ');
    range.insertNode(space);
    range.insertNode(span);
    const selection = window.getSelection();
    if (!selection) {
      this.closeMentionList();
      return;
    }
    const nextRange = document.createRange();
    nextRange.setStartAfter(space);
    nextRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(nextRange);
    this.saveEditorSelection();
    this.closeMentionList();
    this.onEditorInput();
  }

  private buildRangeFromTextIndexes(root: HTMLElement, startIndex: number, endIndex: number) {
    const start = this.findTextPosition(root, startIndex);
    const end = this.findTextPosition(root, endIndex);
    if (!start || !end) return null;
    const range = document.createRange();
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
    return range;
  }

  private findTextPosition(root: HTMLElement, index: number) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let currentIndex = 0;
    while (walker.nextNode()) {
      const node = walker.currentNode as Text;
      const length = node.nodeValue?.length || 0;
      if (currentIndex + length >= index) {
        return { node, offset: index - currentIndex };
      }
      currentIndex += length;
    }
    const fallbackNode = root.lastChild;
    if (fallbackNode && fallbackNode.nodeType === Node.TEXT_NODE) {
      const text = fallbackNode as Text;
      return { node: text, offset: text.nodeValue?.length || 0 };
    }
    return null;
  }

  isUserOnline(userId?: number | null) {
    if (!userId) return false;
    return !!this.users.find((user) => user.id === userId)?.online;
  }

  private focusMessageInput() {
    setTimeout(() => {
      const editor = this.messageInput?.nativeElement;
      if (!editor) return;
      editor.focus();
      this.placeCaretAtEnd(editor);
    }, 0);
  }

  formatUserName(name?: string | null) {
    if (!name) return '';
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 2) {
      return parts.join(' ');
    }
    return `${parts[0]} ${parts[parts.length - 1]}`;
  }

  handleFileSelected(event: Event) {
    const input = event.target as HTMLInputElement | null;
    if (!input?.files?.length) return;
    this.addPendingFiles(Array.from(input.files));
    if (input) {
      input.value = '';
    }
  }

  onMessageDragOver(event: DragEvent) {
    event.preventDefault();
    if (this.isMessageBlocked) return;
    this.draggingMessage = true;
  }

  onMessageDragLeave(event: DragEvent) {
    event.preventDefault();
    const related = event.relatedTarget as HTMLElement | null;
    if (related && this.messageInput?.nativeElement?.contains(related)) {
      return;
    }
    this.draggingMessage = false;
  }

  onMessageDrop(event: DragEvent) {
    event.preventDefault();
    this.draggingMessage = false;
    if (this.isMessageBlocked) return;
    const files = Array.from(event.dataTransfer?.files || []);
    if (!files.length) return;
    this.addPendingFiles(files);
  }

  clearFile(index: number) {
    const target = this.pendingFiles[index];
    if (!target) return;
    this.clearPendingItemPreview(target);
    this.pendingFiles.splice(index, 1);
  }

  async captureScreenAttachment() {
    if (this.isMessageBlocked || this.capturingScreen) {
      return;
    }
    if (!this.selectedConversation) {
      this.snackBar.open('Selecione uma conversa antes de capturar.', 'OK', { duration: 2500 });
      return;
    }
    const desktopBridge = (window as any)?.controlchatDesktop;
    if (desktopBridge?.captureScreen) {
      this.capturingScreen = true;
      try {
        const dataUrl = await desktopBridge.captureScreen();
        const file = this.dataUrlToFile(dataUrl, `print-controlchat-${Date.now()}.png`);
        this.addPendingFiles([file]);
        this.snackBar.open('Print capturado e anexado.', 'OK', { duration: 2200 });
      } catch (_) {
        this.snackBar.open('Falha na captura nativa da tela.', 'OK', { duration: 3000 });
      } finally {
        this.capturingScreen = false;
        this.focusMessageInput();
      }
      return;
    }
    const clipboardApi = (navigator as any)?.clipboard;
    if (!clipboardApi?.read) {
      this.snackBar.open(
        'Tire o print no sistema (Mac: Cmd+Ctrl+Shift+3/4) e cole com Cmd+V no chat.',
        'OK',
        { duration: 4500 }
      );
      return;
    }

    this.capturingScreen = true;
    try {
      const clipboardItems = await clipboardApi.read();
      const files: File[] = [];
      for (const item of clipboardItems) {
        const types = Array.isArray(item.types) ? item.types : [];
        for (const type of types) {
          if (!String(type || '').toLowerCase().startsWith('image/')) continue;
          const blob = await item.getType(type);
          const ext = this.resolveImageExtension(type);
          const file = new File([blob], `print-controlchat-${Date.now()}.${ext}`, { type });
          files.push(file);
        }
      }
      if (!files.length) {
        this.snackBar.open(
          'Nenhum print no clipboard. No Mac use Cmd+Ctrl+Shift+3/4 e tente novamente.',
          'OK',
          { duration: 4200 }
        );
        return;
      }
      this.addPendingFiles(files);
      this.snackBar.open(files.length > 1 ? `${files.length} imagens anexadas.` : 'Imagem anexada.', 'OK', {
        duration: 2200
      });
    } catch (_) {
      this.snackBar.open(
        'Não foi possível ler o clipboard. Depois do print, volte ao chat e use Cmd+V.',
        'OK',
        { duration: 4200 }
      );
    } finally {
      this.capturingScreen = false;
      this.focusMessageInput();
    }
  }

  private dataUrlToFile(dataUrl: string, fileName: string): File {
    const [meta, payload] = String(dataUrl || '').split(',');
    if (!meta || !payload) {
      throw new Error('data-url-invalida');
    }
    const mimeMatch = /data:(.*?);base64/i.exec(meta);
    const mimeType = mimeMatch?.[1] || 'image/png';
    const binary = atob(payload);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return new File([bytes], fileName, { type: mimeType });
  }

  private async captureTrackAsPng(track: MediaStreamTrack): Promise<File> {
    const stream = new MediaStream([track]);
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;

    await video.play();
    await new Promise<void>((resolve) => {
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        resolve();
        return;
      }
      video.onloadeddata = () => resolve();
    });

    const width = video.videoWidth || window.innerWidth || 1920;
    const height = video.videoHeight || window.innerHeight || 1080;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Falha ao criar canvas');
    }
    context.drawImage(video, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((data) => resolve(data), 'image/png');
    });
    video.pause();
    video.srcObject = null;
    stream.getTracks().forEach((item) => item.stop());

    if (!blob) {
      throw new Error('Falha ao gerar imagem');
    }

    return new File([blob], `print-controlchat-${Date.now()}.png`, { type: 'image/png' });
  }

  private addPendingFiles(files: File[]) {
    if (!Array.isArray(files) || !files.length) return;
    files.forEach((file) => this.addPendingFile(file));
  }

  private addPendingFile(file: File) {
    if (!file) return;
    const item = {
      file,
      previewUrl: this.isImageFile(file) ? URL.createObjectURL(file) : null,
      audioPreviewUrl: this.isAudioFile(file) ? URL.createObjectURL(file) : null,
      audioDuration: null as number | null
    };
    this.pendingFiles = [...this.pendingFiles, item];
    if (this.isAudioFile(file)) {
      this.resolveAudioDuration(file).then((duration) => {
        item.audioDuration = duration;
      });
    }
  }

  private clearPendingItemPreview(item: {
    previewUrl: string | null;
    audioPreviewUrl: string | null;
  }) {
    if (item.previewUrl) {
      URL.revokeObjectURL(item.previewUrl);
      item.previewUrl = null;
    }
    if (item.audioPreviewUrl) {
      URL.revokeObjectURL(item.audioPreviewUrl);
      item.audioPreviewUrl = null;
    }
  }

  private clearAllPendingFiles() {
    this.pendingFiles.forEach((item) => this.clearPendingItemPreview(item));
    this.pendingFiles = [];
  }

  private async resolveAudioDuration(file: File): Promise<number | null> {
    try {
      const url = URL.createObjectURL(file);
      const audio = new Audio();
      audio.src = url;
      const duration = await new Promise<number>((resolve, reject) => {
        audio.addEventListener('loadedmetadata', () => resolve(audio.duration));
        audio.addEventListener('error', () => reject(new Error('invalid audio')));
      });
      URL.revokeObjectURL(url);
      return Number.isFinite(duration) ? Math.round(duration) : null;
    } catch {
      return null;
    }
  }

  getMessageQuickReplies(msg: ChatMessage): AssistantQuickReply[] {
    const payload = (msg as any)?.payload;
    if (!payload || typeof payload !== 'object') {
      return [];
    }
    const source = Array.isArray(payload?.quick_replies)
      ? payload.quick_replies
      : Array.isArray(payload?.ui?.quick_replies)
      ? payload.ui.quick_replies
      : [];
    return source
      .map((item: any) => {
        const label = String(item?.label || '').trim();
        if (!label) {
          return null;
        }
        return {
          label,
          value: String(item?.value || label).trim(),
          action: String(item?.action || item?.value || label).trim().toUpperCase()
        } as AssistantQuickReply;
      })
      .filter(Boolean) as AssistantQuickReply[];
  }

  isLatestAssistantMessage(msg: ChatMessage) {
    if (!msg || this.selectedConversation?.type !== 'AI') {
      return false;
    }
    if (!this.messages.length) {
      return false;
    }
    return Number(this.messages[this.messages.length - 1].id) === Number(msg.id);
  }

  onAssistantQuickReplyClick(reply: AssistantQuickReply, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const now = Date.now();
    if (now - this.lastQuickReplyClickAt < 250) {
      return;
    }
    this.lastQuickReplyClickAt = now;
    this.sendAssistantQuickReply(reply);
  }

  sendAssistantQuickReply(reply: AssistantQuickReply) {
    if (!reply || this.selectedConversation?.type !== 'AI') {
      return;
    }
    if (this.sendingAssistantMessage) {
      return;
    }
    this.sendAssistantMessage({
      forcedText: reply.value || reply.label,
      flowAction: reply.action
    });
  }

  private startAssistantLoadingOverlay() {
    this.stopAssistantLoadingOverlay();
    this.sendingAssistantMessage = true;
    this.assistantLoadingMessage = this.assistantLoadingMessages[0];
    let index = 0;
    this.assistantLoadingTimerId = window.setInterval(() => {
      index = (index + 1) % this.assistantLoadingMessages.length;
      this.assistantLoadingMessage = this.assistantLoadingMessages[index];
    }, 1800);
  }

  private stopAssistantLoadingOverlay() {
    this.sendingAssistantMessage = false;
    if (this.assistantLoadingTimerId !== null) {
      window.clearInterval(this.assistantLoadingTimerId);
      this.assistantLoadingTimerId = null;
    }
    this.assistantLoadingMessage = this.assistantLoadingMessages[0];
  }

  private startAssistantProcessingPoll(commandId: number) {
    this.stopAssistantProcessingPoll();
    if (!this.tenantId || !this.userId || !commandId) {
      return;
    }
    this.assistantProcessingCommandId = commandId;
    this.assistantProcessingPollId = window.setInterval(() => {
      if (
        !this.selectedConversation ||
        this.selectedConversation.type !== 'AI' ||
        Number(this.selectedConversation.assistant_command_id || 0) !== Number(commandId)
      ) {
        this.stopAssistantProcessingPoll();
        return;
      }

      this.chatService.getAiAssistantMessages(this.tenantId!, this.userId!, commandId).subscribe({
        next: (payload) => {
          if (!this.selectedConversation || this.selectedConversation.type !== 'AI') {
            this.stopAssistantProcessingPoll();
            return;
          }
          const rows = Array.isArray(payload?.messages) ? payload.messages : [];
          const mapped = rows.map((item: any) => this.normalizeMessage(item));
          this.messages = this.uniqueMessages(mapped);
          this.hasMoreMessages = false;
          this.oldestMessageId = this.messages.length ? this.messages[0].id : null;
          this.updateMessageMatches();
          this.scrollMessagesToBottom();

          const flowState = String(payload?.thread?.flow_state || '').toUpperCase();
          if (flowState !== 'PROCESSING_SCOPE') {
            this.stopAssistantProcessingPoll();
            this.loadAssistants();
          }
        },
        error: () => {
          this.stopAssistantProcessingPoll();
        }
      });
    }, 3500);
  }

  private stopAssistantProcessingPoll() {
    if (this.assistantProcessingPollId !== null) {
      window.clearInterval(this.assistantProcessingPollId);
      this.assistantProcessingPollId = null;
    }
    this.assistantProcessingCommandId = null;
  }

  private sendAssistantMessage(options?: { forcedText?: string; flowAction?: string }) {
    if (!this.tenantId || !this.userId || !this.selectedConversation || this.selectedConversation.type !== 'AI') {
      return;
    }
    if (this.sendingAssistantMessage) {
      return;
    }

    const commandId = Number(
      this.selectedConversation.assistant_command_id ||
      Math.abs(Number(this.selectedConversation.id || 0)) ||
      0
    );
    if (!commandId) {
      this.snackBar.open('Assistente inválido para esta conversa.', 'OK', { duration: 2600 });
      return;
    }

    const forcedText = String(options?.forcedText || '').trim();
    const flowAction = String(options?.flowAction || '').trim();
    const useQuickAction = !!flowAction;
    const rawHtml = useQuickAction ? '' : this.getEditorHtml();
    const sanitizedHtml = useQuickAction ? '' : this.sanitizeMessageHtml(rawHtml);
    const plainText = useQuickAction ? forcedText : this.extractPlainText(sanitizedHtml).trim();
    const files = useQuickAction ? [] : this.pendingFiles.map((item) => item.file).filter(Boolean);
    if (!plainText && !files.length && !flowAction) {
      return;
    }

    const nonImageFiles = files.filter((file) => !this.isImageFile(file));
    if (nonImageFiles.length) {
      this.snackBar.open('Assistente virtual aceita apenas anexos de imagem.', 'OK', { duration: 3200 });
      return;
    }

    this.startAssistantLoadingOverlay();
    const executeSend = (uploadedImages: Array<{ name?: string; url: string; mime_type?: string | null; size?: number | null }>) => {
      this.chatService
        .sendAiAssistantMessage(this.tenantId!, this.userId!, commandId, plainText, {
          images: uploadedImages,
          flow_action: flowAction || null
        })
        .subscribe({
          next: (response) => {
            this.messageText = '';
            this.setEditorHtml('');
            this.clearAllPendingFiles();
            this.replyToMessage = null;

            const rows = Array.isArray(response?.messages) ? response.messages : [];
            const mapped = rows.map((item: any) => this.normalizeMessage(item));
            this.messages = this.uniqueMessages(mapped);
            this.hasMoreMessages = false;
            this.oldestMessageId = this.messages.length ? this.messages[0].id : null;
            this.updateMessageMatches();
            this.scrollMessagesToBottom();

            const nowIso = new Date().toISOString();
            const resultText = String(response?.transaction?.result_text || '').trim();
            this.assistants = this.assistants.map((assistant) => {
              if (Number(assistant.command_id) !== commandId) {
                return assistant;
              }
              return {
                ...assistant,
                current_transaction_id:
                  Number(response?.transaction?.id || assistant.current_transaction_id || 0) || null,
                last_activity_at: nowIso,
                last_message_text: resultText || plainText || assistant.last_message_text || ''
              };
            });

            const selected = this.assistants.find((assistant) => Number(assistant.command_id) === commandId);
            if (selected && this.selectedConversation?.type === 'AI') {
              this.selectedConversation = this.assistantToConversation(selected);
            }
            const flowState = String(response?.thread?.flow_state || '').toUpperCase();
            if (flowState === 'PROCESSING_SCOPE') {
              this.startAssistantProcessingPoll(commandId);
            } else {
              this.stopAssistantProcessingPoll();
            }
            this.loadAssistants();
          },
          error: (error) => {
            this.stopAssistantLoadingOverlay();
            this.snackBar.open(
              error?.error?.error || 'Não foi possível processar a conversa com o assistente.',
              'OK',
              { duration: 3500 }
            );
          },
          complete: () => {
            this.stopAssistantLoadingOverlay();
          }
        });
    };

    if (!files.length) {
      executeSend([]);
      return;
    }

    this.chatService.uploadAiTransactionImages(this.tenantId, this.userId, files).subscribe({
      next: (uploadResponse) => {
        const uploadedImages = Array.isArray(uploadResponse?.files) ? uploadResponse.files : [];
        executeSend(uploadedImages);
      },
      error: (error) => {
        this.stopAssistantLoadingOverlay();
        this.snackBar.open(error?.error?.error || 'Falha ao enviar imagens para o assistente.', 'OK', {
          duration: 3200
        });
      }
    });
  }

  sendMessage() {
    if (!this.tenantId || !this.userId || !this.selectedConversation) return;
    if (this.selectedConversation.type === 'AI') {
      this.sendAssistantMessage();
      return;
    }
    if (this.isMessageBlocked) {
      this.snackBar.open('Somente administradores podem enviar mensagens neste grupo.', 'OK', { duration: 3000 });
      return;
    }
    if (this.isRecording) {
      this.autoSendRecording = true;
      this.stopRecording();
      return;
    }
    const rawHtml = this.getEditorHtml();
    const sanitizedHtml = this.sanitizeMessageHtml(rawHtml);
    const plainText = this.extractPlainText(sanitizedHtml);
    if (!plainText.trim() && !this.pendingFiles.length) {
      return;
    }
    const message = sanitizedHtml;
    const replyToId = this.replyToMessage?.id || null;
    const files = this.pendingFiles.map((item) => item.file);
    const firstAttachment = this.pendingFiles[0] || null;
    const audioDuration =
      this.pendingFiles.length === 1 && firstAttachment && this.isAudioFile(firstAttachment.file)
        ? firstAttachment.audioDuration ?? null
        : null;
    this.chatService
      .sendMessage(
        this.tenantId,
        this.userId,
        this.selectedConversation.id,
        message,
        files,
        replyToId,
        audioDuration
      )
      .subscribe({
        next: (response) => {
          this.messageText = '';
          this.setEditorHtml('');
          this.clearAllPendingFiles();
          this.replyToMessage = null;
          if (response && response.conversation_id === this.selectedConversation?.id) {
            this.addMessageUnique(response);
            this.scrollMessagesToBottom();
          }
          if (response) {
            this.updateConversationOnMessage(response);
          }
          this.loadConversations();
        }
      });
  }

  private scrollMessagesToBottom(force = false) {
    if (!force && !this.userNearBottom) {
      return;
    }
    setTimeout(() => {
      const el = this.messagesContainer?.nativeElement;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    }, 0);
  }

  private normalizeMessage(message: ChatMessage) {
    const rawPayload = (message as any)?.payload;
    let normalizedPayload: any = null;
    if (rawPayload && typeof rawPayload === 'string') {
      try {
        normalizedPayload = JSON.parse(rawPayload);
      } catch (_error) {
        normalizedPayload = null;
      }
    } else if (rawPayload && typeof rawPayload === 'object') {
      normalizedPayload = rawPayload;
    }

    const payloadAttachments = Array.isArray((message as any).attachments)
      ? (message as any).attachments
      : [];
    const attachments: ChatMessageAttachment[] = payloadAttachments.length
      ? payloadAttachments.map((item: any) => ({
          id: item?.id ? Number(item.id) : null,
          message_id: item?.message_id ? Number(item.message_id) : Number(message.id),
          file_name: item?.file_name || null,
          file_path: item?.file_path || null,
          file_type: item?.file_type || null,
          file_size: item?.file_size ? Number(item.file_size) : null,
          created_at: item?.created_at || null
        }))
      : (message.file_path
          ? [{
              id: null,
              message_id: Number(message.id),
              file_name: message.file_name || null,
              file_path: message.file_path || null,
              file_type: message.file_type || null,
              file_size: message.file_size ? Number(message.file_size) : null,
              created_at: message.created_at || null
            }]
          : []);
    return {
      ...message,
      id: Number(message.id),
      conversation_id: Number(message.conversation_id),
      sender_id: Number(message.sender_id),
      file_type: message.file_type || null,
      file_size: Number((message as any).file_size || 0) || null,
      read_count: Number((message as any).read_count || 0),
      attachments,
      payload: normalizedPayload
    } as ChatMessage;
  }

  private uniqueMessages(list: ChatMessage[]) {
    const map = new Map<number, ChatMessage>();
    list.forEach((msg) => {
      const normalized = this.normalizeMessage(msg);
      if (!map.has(normalized.id)) {
        map.set(normalized.id, normalized);
      }
    });
    return Array.from(map.values()).sort((a, b) => a.id - b.id);
  }

  private addMessageUnique(message: ChatMessage) {
    const normalized = this.normalizeMessage(message);
    const exists = this.messages.some((item) => Number(item.id) === normalized.id);
    if (exists) return;
    this.messages = [...this.messages, normalized];
    this.updateMessageMatches();
  }

  get hasUnreadMessages() {
    return this.unreadConversationIds.size > 0;
  }

  isConversationUnread(conversationId?: number | null) {
    if (!conversationId) return false;
    return this.unreadConversationIds.has(conversationId);
  }

  isMessageSeen(message: ChatMessage) {
    if (!this.userId || !message) return false;
    if (message.sender_id !== this.userId) return false;
    return this.getMessageReadCount(message) > 0;
  }

  getMessageReadCount(message: ChatMessage) {
    const base = Number(message?.read_count || 0);
    if (this.selectedConversation?.type === 'GROUP') {
      const members = Number(this.selectedConversation?.members_count || 0);
      if (members > 0) {
        return Math.min(base, Math.max(0, members - 1));
      }
    }
    return base;
  }

  isGroupMessage(message: ChatMessage) {
    const conv = this.conversations.find((item) => item.id === message.conversation_id) || this.selectedConversation;
    return conv?.type === 'GROUP';
  }

  private markConversationUnread(conversationId: number) {
    this.unreadConversationIds.add(conversationId);
    this.lastIncomingConversationId = conversationId;
    this.conversations = this.conversations.map((conv) =>
      conv.id === conversationId ? { ...conv, has_unread: 1 } : conv
    );
    this.updateBrowserTitle();
  }

  private clearConversationUnread(conversationId: number) {
    if (this.unreadConversationIds.has(conversationId)) {
      this.unreadConversationIds.delete(conversationId);
    }
    if (this.lastIncomingConversationId === conversationId) {
      this.lastIncomingConversationId = null;
    }
    this.conversations = this.conversations.map((conv) =>
      conv.id === conversationId ? { ...conv, has_unread: 0 } : conv
    );
    this.updateBrowserTitle();
  }

  private syncUnreadConversations() {
    const nextUnread = new Set<number>();
    this.conversations.forEach((conv) => {
      const flag = conv.has_unread;
      if (flag === true || Number(flag || 0) === 1) {
        nextUnread.add(conv.id);
      }
    });
    this.unreadConversationIds = nextUnread;
    if (this.lastIncomingConversationId && !nextUnread.has(this.lastIncomingConversationId)) {
      this.lastIncomingConversationId = null;
    }
    this.updateBrowserTitle();
  }

  private markConversationRead(conversationId: number) {
    if (!this.tenantId || !this.userId || !conversationId) return;
    this.chatService.markConversationRead(this.tenantId, this.userId, conversationId).subscribe({
      next: () => {
        const now = new Date().toISOString();
        this.conversations = this.conversations.map((conv) =>
          conv.id === conversationId ? { ...conv, self_last_read_at: now, has_unread: 0, mentioned: 0 } : conv
        );
      }
    });
  }

  private updateBrowserTitle() {
    const base = this.tenantName ? `CPS - ${this.tenantName}` : 'CPS';
    if (!this.canView) {
      this.titleService.setTitle(base);
      return;
    }
    const count = this.unreadConversationIds.size;
    if (count > 0) {
      this.titleService.setTitle(`(${count}) ${base}`);
      return;
    }
    this.titleService.setTitle(base);
  }

  private updateConversationOnMessage(message: ChatMessage) {
    const idx = this.conversations.findIndex((conv) => conv.id === message.conversation_id);
    if (idx === -1) {
      this.loadConversations();
      return;
    }
    const existing = this.conversations[idx];
    const mentioned = this.isMentionForUser(message.message_text || '');
    const updated: ChatConversation = {
      ...existing,
      last_message_at: message.created_at || existing.last_message_at || null,
      last_activity_at: message.created_at || existing.last_activity_at || null,
      last_message_text: message.message_text ?? null,
      last_message_file_name: message.file_name ?? null,
      last_message_file_path: message.file_path ?? null,
      mentioned: mentioned ? 1 : 0
    };
    this.conversations = [
      updated,
      ...this.conversations.slice(0, idx),
      ...this.conversations.slice(idx + 1)
    ];
  }

  private isMentionForUser(messageText: string) {
    if (!this.userId) return false;
    const text = String(messageText || '');
    if (text.includes('data-mention="all"')) {
      return true;
    }
    return text.includes(`data-mention-id="${this.userId}"`);
  }

  isConversationMentioned(conv: ChatConversation) {
    const mentioned = conv.mentioned === true || Number(conv.mentioned || 0) === 1;
    return mentioned;
  }

  get filteredMessages() {
    return this.messages;
  }

  toggleMessageSearch() {
    this.showMessageSearch = !this.showMessageSearch;
    if (!this.showMessageSearch) {
      this.messageSearchTerm = '';
      this.updateMessageMatches();
      return;
    }
    setTimeout(() => this.messageSearchInput?.nativeElement.focus(), 0);
  }

  onMessageSearchChange() {
    const term = this.messageSearchTerm.trim();
    if (!term) {
      this.updateMessageMatches();
      return;
    }
    if (!this.tenantId || !this.userId || !this.selectedConversation) {
      this.updateMessageMatches(true);
      return;
    }
    if (this.selectedConversation.type === 'AI') {
      this.updateMessageMatches(true);
      return;
    }
    this.messageSearchLoading = true;
    const conversationId = this.selectedConversation.id;
    this.chatService.searchMessages(this.tenantId, this.userId, conversationId, term).subscribe({
      next: (payload) => {
        if (!this.selectedConversation || this.selectedConversation.id !== conversationId) {
          return;
        }
        this.messageMatchIds = payload?.message_ids || [];
        this.activeMatchIndex = 0;
        this.messageSearchLoading = false;
        this.updateMessageMatches(true);
      },
      error: () => {
        this.messageSearchLoading = false;
        this.updateMessageMatches(true);
      }
    });
  }

  get messageMatchCount() {
    return this.messageMatchIds.length;
  }

  get messageMatchLabel() {
    const total = this.messageMatchIds.length;
    if (!total) return '0 / 0';
    return `${this.activeMatchIndex + 1} / ${total}`;
  }

  isMessageMatch(msg: ChatMessage) {
    return this.messageMatchIds.includes(msg.id);
  }

  isActiveMessageMatch(msg: ChatMessage) {
    const activeId = this.messageMatchIds[this.activeMatchIndex];
    return activeId === msg.id;
  }

  goToNextMatch() {
    const total = this.messageMatchIds.length;
    if (!total) return;
    this.activeMatchIndex = (this.activeMatchIndex + 1) % total;
    this.ensureMessageLoadedAndScroll(this.messageMatchIds[this.activeMatchIndex]);
  }

  goToPrevMatch() {
    const total = this.messageMatchIds.length;
    if (!total) return;
    this.activeMatchIndex = (this.activeMatchIndex - 1 + total) % total;
    this.ensureMessageLoadedAndScroll(this.messageMatchIds[this.activeMatchIndex]);
  }

  private updateMessageMatches(scrollToFirst = false) {
    const term = this.messageSearchTerm.trim().toLowerCase();
    if (!term) {
      this.messageMatchIds = [];
      this.activeMatchIndex = 0;
      return;
    }
    if (!this.messageMatchIds.length) {
      this.messageMatchIds = this.messages
        .filter((msg) => this.isMessageMatchingTerm(msg, term))
        .map((msg) => msg.id);
    }
    if (this.activeMatchIndex >= this.messageMatchIds.length) {
      this.activeMatchIndex = 0;
    }
    if (scrollToFirst && this.messageMatchIds.length) {
      this.ensureMessageLoadedAndScroll(this.messageMatchIds[this.activeMatchIndex]);
    }
  }

  private ensureMessageLoadedAndScroll(messageId: number) {
    if (this.messages.some((msg) => msg.id === messageId)) {
      this.scrollToMessage(messageId);
      return;
    }
    this.loadUntilMessage(messageId);
  }

  private loadUntilMessage(messageId: number) {
    if (!this.selectedConversation || !this.tenantId || !this.userId) return;
    if (!this.hasMoreMessages || this.loadingOlderMessages || !this.oldestMessageId) return;
    const conversationId = this.selectedConversation.id;
    const container = this.messagesContainer?.nativeElement;
    const previousHeight = container?.scrollHeight || 0;
    this.loadingOlderMessages = true;
    this.chatService
      .getMessages(this.tenantId, this.userId, conversationId, this.messagePageSize, this.oldestMessageId)
      .subscribe({
        next: (payload) => {
          if (!this.selectedConversation || this.selectedConversation.id !== conversationId) {
            this.loadingOlderMessages = false;
            return;
          }
          const rows = payload?.messages || [];
          const filtered = rows.filter((msg) => msg?.conversation_id === conversationId);
          if (filtered.length) {
            this.messages = [...filtered, ...this.messages];
            this.oldestMessageId = this.messages[0]?.id || this.oldestMessageId;
          }
          this.hasMoreMessages = !!payload?.has_more;
          this.loadingOlderMessages = false;
          this.updateMessageMatches();
          setTimeout(() => {
            const el = this.messagesContainer?.nativeElement;
            if (el) {
              const newHeight = el.scrollHeight;
              el.scrollTop = newHeight - previousHeight;
            }
            if (this.messages.some((msg) => msg.id === messageId)) {
              this.scrollToMessage(messageId);
              return;
            }
            if (this.hasMoreMessages) {
              this.loadUntilMessage(messageId);
            }
          }, 0);
        },
        error: () => {
          this.loadingOlderMessages = false;
        }
      });
  }

  private isMessageMatchingTerm(msg: ChatMessage, term: string) {
    const text = this.extractPlainText(String(msg.message_text || '')).toLowerCase();
    const sender = String(msg.sender_name || '').toLowerCase();
    const attachmentNames = (msg.attachments || [])
      .map((item) => String(item?.file_name || '').toLowerCase())
      .join(' ');
    return text.includes(term) || sender.includes(term) || attachmentNames.includes(term);
  }

  private scrollToMessage(messageId: number) {
    setTimeout(() => {
      const el = this.messagesContainer?.nativeElement.querySelector(`#msg-${messageId}`) as HTMLElement | null;
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  getConversationPreview(conv: ChatConversation) {
    if ((conv.mentioned === true || Number(conv.mentioned || 0) === 1) && this.isConversationUnread(conv.id)) {
      return 'Você foi mencionado';
    }
    const fileType = String(conv.last_message_file_type || '').toLowerCase();
    if (fileType.startsWith('audio/')) {
      return this.audioPreviewLabel(conv.last_message_audio_duration);
    }
    const text = this.extractPlainText(String(conv.last_message_text || ''));
    if (text) {
      return this.truncatePreview(text);
    }
    if (conv.last_message_file_name) {
      if (this.isAudioFileName(conv.last_message_file_name)) {
        return this.audioPreviewLabel(conv.last_message_audio_duration);
      }
      return this.truncatePreview(`Arquivo: ${conv.last_message_file_name}`);
    }
    if (conv.last_message_file_path) {
      return this.truncatePreview('Arquivo enviado');
    }
    return '';
  }

  toggleHeaderMenu() {
    this.isHeaderMenuOpen = !this.isHeaderMenuOpen;
    if (!this.isHeaderMenuOpen) {
      this.headerMenuView = 'root';
    }
  }

  onSoundToggle(enabled: boolean) {
    if (this.embedded) {
      return;
    }
    this.soundEnabled = !!enabled;
    try {
      localStorage.setItem('controlchat-sound-enabled', this.soundEnabled ? '1' : '0');
    } catch (_) {}
    if (this.soundEnabled) {
      this.unlockAudioContext();
    }
  }

  private loadSoundPreference() {
    if (this.embedded) {
      this.forceDesktopSilentMode();
      return;
    }
    try {
      const raw = localStorage.getItem('controlchat-sound-enabled');
      if (raw === null) {
        this.soundEnabled = true;
        return;
      }
      this.soundEnabled = raw === '1';
    } catch (_) {
      this.soundEnabled = true;
    }
  }

  private playNotificationSound() {
    if (this.embedded) {
      return;
    }
    try {
      if (this.notificationAudio) {
        this.notificationAudio.currentTime = 0;
        const playPromise = this.notificationAudio.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => undefined);
        }
        return;
      }
      if (!this.audioContext) {
        this.audioContext = new AudioContext();
      }
      const ctx = this.audioContext;
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => undefined);
      }
      if (!this.audioUnlocked) {
        return;
      }
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.26);
    } catch (_) {
      // ignore audio errors
    }
  }

  private forceDesktopSilentMode() {
    this.soundEnabled = false;
    try {
      localStorage.setItem('controlchat-sound-enabled', '0');
    } catch (_) {}
  }

  private unlockAudioContext() {
    try {
      if (!this.notificationAudio) {
        this.notificationAudio = new Audio('/assets/notify.wav');
        this.notificationAudio.volume = 0.6;
      }
      if (!this.audioContext) {
        this.audioContext = new AudioContext();
      }
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume().catch(() => undefined);
      }
      this.audioUnlocked = true;
      this.detachAudioUnlockListeners();
    } catch (_) {
      // ignore
    }
  }

  private attachAudioUnlockListeners() {
    const events = ['click', 'keydown', 'touchstart'];
    events.forEach((evt) => window.addEventListener(evt, this.unlockHandler, { passive: true }));
  }

  private detachAudioUnlockListeners() {
    const events = ['click', 'keydown', 'touchstart'];
    events.forEach((evt) => window.removeEventListener(evt, this.unlockHandler));
  }

  openCreateGroupFromMenu() {
    this.headerMenuView = 'create-group';
    this.isCreatingGroup = true;
    this.scrollToGroupForm();
  }

  openKeywordsFromMenu() {
    this.headerMenuView = 'keywords';
    this.isCreatingGroup = false;
    this.scrollToMonitoring();
  }

  closeHeaderMenu() {
    this.isHeaderMenuOpen = false;
    this.headerMenuView = 'root';
  }

  backHeaderMenu() {
    this.headerMenuView = 'root';
  }

  private scrollToGroupForm() {
    setTimeout(() => this.groupForm?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  }

  private scrollToMonitoring() {
    setTimeout(() => this.monitoringSection?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  }

  formatConversationTime(value?: string | null) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((startToday.getTime() - startDate.getTime()) / 86400000);
    if (diffDays === 0) {
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
    if (diffDays === 1) {
      return 'Ontem';
    }
    if (diffDays > 1 && diffDays < 7) {
      return date.toLocaleDateString('pt-BR', { weekday: 'long' });
    }
    return date.toLocaleDateString('pt-BR');
  }

  private truncatePreview(value: string, maxWords = 8, maxChars = 72) {
    const normalized = String(value || '').replace(/\s+/g, ' ').trim();
    if (!normalized) {
      return '';
    }
    let result = normalized;
    let truncated = false;
    const words = normalized.split(' ');
    if (words.length > maxWords) {
      result = words.slice(0, maxWords).join(' ');
      truncated = true;
    }
    if (result.length > maxChars) {
      const slice = result.slice(0, maxChars).trimEnd();
      const lastSpace = slice.lastIndexOf(' ');
      result = lastSpace > Math.floor(maxChars * 0.6) ? slice.slice(0, lastSpace) : slice;
      truncated = true;
    }
    return truncated ? `${result} (..)` : result;
  }

  getHighlightedMessage(msg: ChatMessage): SafeHtml {
    const rawHtml = String(msg.message_text || '');
    const term = this.messageSearchTerm.trim();
    const sanitized = this.sanitizeMessageHtml(rawHtml);
    if (!term) {
      return this.sanitizer.bypassSecurityTrustHtml(sanitized);
    }
    const highlighted = this.highlightHtmlText(sanitized, term);
    return this.sanitizer.bypassSecurityTrustHtml(highlighted);
  }

  getReplyPreviewHtml(msg: ChatMessage): SafeHtml {
    if (!msg) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }
    if (msg.reply_message_text) {
      const sanitized = this.sanitizeMessageHtml(String(msg.reply_message_text || ''));
      return this.sanitizer.bypassSecurityTrustHtml(sanitized);
    }
    const replyType = String(msg.reply_file_type || '');
    const replyName = String(msg.reply_file_name || '');
    if (replyType.startsWith('audio/') || this.isAudioFileName(replyName)) {
      return this.sanitizer.bypassSecurityTrustHtml(this.escapeHtml(this.audioPreviewLabel(msg.reply_audio_duration)));
    }
    const fallback = msg.reply_file_name || 'Arquivo';
    return this.sanitizer.bypassSecurityTrustHtml(this.escapeHtml(String(fallback)));
  }

  private escapeHtml(value: string) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private sanitizeMessageHtml(html: string) {
    if (!html) return '';
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild as HTMLElement | null;
    if (!root) return '';
    const allowedTags = new Set([
      'B',
      'STRONG',
      'I',
      'EM',
      'U',
      'S',
      'BR',
      'UL',
      'OL',
      'LI',
      'SPAN',
      'P',
      'DIV'
    ]);
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
    const toProcess: Element[] = [];
    while (walker.nextNode()) {
      toProcess.push(walker.currentNode as Element);
    }
    toProcess.forEach((el) => {
      const element = el as HTMLElement;
      if (!allowedTags.has(element.tagName)) {
        const parent = el.parentNode;
        if (!parent) return;
        while (el.firstChild) {
          parent.insertBefore(el.firstChild, el);
        }
        parent.removeChild(el);
        return;
      }
      const style = element.getAttribute('style');
      const allowedAttrs = new Set(['style']);
      if (element.tagName === 'SPAN' && element.classList.contains('mention-tagged')) {
        allowedAttrs.add('class');
        allowedAttrs.add('data-mention-id');
        allowedAttrs.add('data-mention');
      }
      Array.from(element.attributes).forEach((attr) => {
        if (!allowedAttrs.has(attr.name)) {
          element.removeAttribute(attr.name);
        }
      });
      if (style) {
        const nextStyles: string[] = [];
        const color = element.style.color;
        const background = element.style.backgroundColor;
        if (color) {
          nextStyles.push(`color: ${color}`);
        }
        if (background) {
          nextStyles.push(`background-color: ${background}`);
        }
        if (nextStyles.length) {
          element.setAttribute('style', nextStyles.join('; '));
        } else {
          element.removeAttribute('style');
        }
      }
    });
    return root.innerHTML;
  }

  private extractPlainText(html: string) {
    if (!html) return '';
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!root) return '';
    return (root.textContent || '').replace(/\u00a0/g, ' ');
  }

  private highlightHtmlText(html: string, term: string) {
    if (!html) return '';
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild as HTMLElement | null;
    if (!root) return html;
    const regex = new RegExp(this.escapeRegExp(term), 'gi');
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes: Text[] = [];
    while (walker.nextNode()) {
      nodes.push(walker.currentNode as Text);
    }
    nodes.forEach((node) => {
      const text = node.nodeValue || '';
      regex.lastIndex = 0;
      if (!regex.test(text)) {
        return;
      }
      regex.lastIndex = 0;
      const frag = doc.createDocumentFragment();
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      while ((match = regex.exec(text))) {
        if (match.index > lastIndex) {
          frag.append(text.slice(lastIndex, match.index));
        }
        const span = doc.createElement('span');
        span.className = 'match-highlight';
        span.textContent = match[0];
        frag.append(span);
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < text.length) {
        frag.append(text.slice(lastIndex));
      }
      node.parentNode?.replaceChild(frag, node);
    });
    return root.innerHTML;
  }

  private escapeRegExp(value: string) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  fileExtLabel(name?: string | null) {
    const ext = this.extractFileExt(name);
    if (!ext) return 'ARQ';
    return ext.toUpperCase();
  }

  truncateFileName(name: string, max = 22) {
    const clean = String(name || '').trim();
    if (!clean) return '';
    if (clean.length <= max) return clean;
    return `${clean.slice(0, max - 3)}...`;
  }

  private extractFileExt(name?: string | null) {
    if (!name) return '';
    const clean = name.trim();
    const parts = clean.split('.');
    if (parts.length < 2) return '';
    return parts[parts.length - 1];
  }

  isImageFile(file?: File | null) {
    if (!file) return false;
    return file.type?.startsWith('image/');
  }

  isImageUrl(url?: string | null) {
    if (!url) return false;
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(url.split('?')[0]);
  }

  isAudioFile(file?: File | null) {
    if (!file) return false;
    return file.type?.startsWith('audio/');
  }

  isAudioUrl(url?: string | null) {
    if (!url) return false;
    return /\.(mp3|wav|ogg|webm|m4a|aac)$/i.test(url.split('?')[0]);
  }

  async startRecording() {
    if (this.isRecording) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      this.snackBar.open('Gravação de áudio não suportada neste navegador.', 'OK', { duration: 3000 });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const options: MediaRecorderOptions = {};
      if (MediaRecorder.isTypeSupported('audio/webm')) {
        options.mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
        options.mimeType = 'audio/ogg';
      }
      const recorder = new MediaRecorder(stream, options);
      const chunks: BlobPart[] = [];
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size) {
          chunks.push(event.data);
        }
      };
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
        const ext = recorder.mimeType?.includes('ogg') ? 'ogg' : 'webm';
        const file = new File([blob], `audio-${Date.now()}.${ext}`, { type: blob.type });
        this.addPendingFiles([file]);
        stream.getTracks().forEach((track) => track.stop());
        if (this.autoSendRecording) {
          this.autoSendRecording = false;
          setTimeout(() => this.sendMessage(), 0);
        }
      };
      recorder.start();
      this.mediaRecorder = recorder;
      this.isRecording = true;
      window.addEventListener('keydown', this.recordingKeyHandler);
      this.recordingSeconds = 0;
      this.recordingTimerId = window.setInterval(() => {
        this.recordingSeconds += 1;
      }, 1000);
    } catch (error) {
      this.snackBar.open('Não foi possível acessar o microfone.', 'OK', { duration: 3000 });
    }
  }

  stopRecording() {
    if (!this.mediaRecorder || !this.isRecording) return;
    window.removeEventListener('keydown', this.recordingKeyHandler);
    this.mediaRecorder.stop();
    this.mediaRecorder = null;
    this.isRecording = false;
    if (this.recordingTimerId) {
      window.clearInterval(this.recordingTimerId);
      this.recordingTimerId = null;
    }
  }

  cancelRecording() {
    if (!this.mediaRecorder || !this.isRecording) return;
    this.autoSendRecording = false;
    window.removeEventListener('keydown', this.recordingKeyHandler);
    this.mediaRecorder.stop();
    this.mediaRecorder = null;
    this.isRecording = false;
    if (this.recordingTimerId) {
      window.clearInterval(this.recordingTimerId);
      this.recordingTimerId = null;
    }
    this.recordingSeconds = 0;
  }

  formatRecordingTime(seconds: number) {
    const safe = Math.max(0, Math.floor(Number(seconds || 0)));
    const mins = Math.floor(safe / 60);
    const secs = safe % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private isAudioFileName(name?: string | null) {
    if (!name) return false;
    return /\.(mp3|wav|ogg|webm|m4a|aac)$/i.test(name);
  }

  getMessageAttachments(message: ChatMessage | null) {
    if (!message) return [];
    return Array.isArray(message.attachments) ? message.attachments : [];
  }

  isImageAttachment(item: ChatMessageAttachment | null) {
    if (!item?.file_path) return false;
    const mime = String(item.file_type || '').toLowerCase();
    if (mime.startsWith('image/')) return true;
    return this.isImageUrl(item.file_path);
  }

  isAudioAttachment(item: ChatMessageAttachment | null) {
    if (!item?.file_path) return false;
    const mime = String(item.file_type || '').toLowerCase();
    if (mime.startsWith('audio/')) return true;
    return this.isAudioUrl(item.file_path);
  }

  getAudioTrackKey(message: ChatMessage, attachment: ChatMessageAttachment, index = 0) {
    const attachmentId = Number(attachment?.id || 0);
    const messageId = Number(message?.id || 0);
    if (attachmentId > 0) {
      return `msg:${messageId}:att:${attachmentId}`;
    }
    const path = String(attachment?.file_path || attachment?.file_name || '').trim();
    if (!path) {
      return `msg:${messageId}:idx:${index}`;
    }
    return `msg:${messageId}:file:${path}`;
  }

  toggleAudioPlayback(player: HTMLAudioElement, key: string) {
    if (!player || !key) return;
    if (this.activeAudioElement && this.activeAudioElement !== player) {
      this.activeAudioElement.pause();
    }
    if (player.paused) {
      player.play().catch(() => undefined);
      return;
    }
    player.pause();
  }

  onAudioLoadedMetadata(player: HTMLAudioElement, key: string, fallbackDuration?: number | null) {
    const state = this.ensureAudioPlaybackState(key);
    const metaDuration = Number(player?.duration || 0);
    const fallback = Number(fallbackDuration || 0);
    state.duration = Number.isFinite(metaDuration) && metaDuration > 0 ? metaDuration : Math.max(0, fallback);
  }

  onAudioTimeUpdate(player: HTMLAudioElement, key: string) {
    const state = this.ensureAudioPlaybackState(key);
    state.current = Math.max(0, Number(player?.currentTime || 0));
  }

  onAudioPlay(player: HTMLAudioElement, key: string) {
    const state = this.ensureAudioPlaybackState(key);
    state.playing = true;
    this.activeAudioElement = player;
    this.activeAudioKey = key;
  }

  onAudioPause(player: HTMLAudioElement, key: string) {
    const state = this.ensureAudioPlaybackState(key);
    state.playing = false;
    if (this.activeAudioElement === player) {
      this.activeAudioElement = null;
      if (this.activeAudioKey === key) {
        this.activeAudioKey = null;
      }
    }
  }

  onAudioEnded(player: HTMLAudioElement, key: string) {
    const state = this.ensureAudioPlaybackState(key);
    state.playing = false;
    state.current = Math.max(0, Number(player?.duration || state.duration || 0));
    if (this.activeAudioElement === player) {
      this.activeAudioElement = null;
      if (this.activeAudioKey === key) {
        this.activeAudioKey = null;
      }
    }
  }

  isAudioPlaying(key: string) {
    return !!this.audioPlaybackState.get(key)?.playing;
  }

  getAudioCurrentLabel(key: string) {
    const state = this.audioPlaybackState.get(key);
    return this.formatRecordingTime(Number(state?.current || 0));
  }

  getAudioDurationLabel(key: string, fallbackDuration?: number | null) {
    const state = this.audioPlaybackState.get(key);
    const duration = Number(state?.duration || fallbackDuration || 0);
    return this.formatRecordingTime(duration);
  }

  getAudioWaveBars(key: string) {
    const cached = this.audioWaveCache.get(key);
    if (cached?.length) {
      return cached;
    }
    const bars = this.generateAudioWaveBars(key);
    this.audioWaveCache.set(key, bars);
    return bars;
  }

  isAudioWaveBarActive(key: string, barIndex: number, barsLength: number) {
    if (!barsLength) return false;
    const state = this.audioPlaybackState.get(key);
    if (!state?.duration || state.duration <= 0) {
      return false;
    }
    const progress = Math.max(0, Math.min(1, state.current / state.duration));
    const activeBars = Math.round(progress * barsLength);
    return barIndex < activeBars;
  }

  seekAudioFromWave(event: MouseEvent, player: HTMLAudioElement, key: string) {
    if (!player) return;
    const target = event.currentTarget as HTMLElement | null;
    if (!target) return;
    const duration = Number(player.duration || this.audioPlaybackState.get(key)?.duration || 0);
    if (!duration || !Number.isFinite(duration)) {
      return;
    }
    const rect = target.getBoundingClientRect();
    const raw = (event.clientX - rect.left) / rect.width;
    const ratio = Math.max(0, Math.min(1, raw));
    const nextTime = duration * ratio;
    player.currentTime = nextTime;
    const state = this.ensureAudioPlaybackState(key);
    state.current = nextTime;
  }

  private ensureAudioPlaybackState(key: string) {
    let state = this.audioPlaybackState.get(key);
    if (!state) {
      state = { duration: 0, current: 0, playing: false };
      this.audioPlaybackState.set(key, state);
    }
    return state;
  }

  private stopActiveAudioPlayback() {
    if (this.activeAudioElement) {
      this.activeAudioElement.pause();
      this.activeAudioElement = null;
    }
    if (this.activeAudioKey) {
      const state = this.audioPlaybackState.get(this.activeAudioKey);
      if (state) {
        state.playing = false;
      }
      this.activeAudioKey = null;
    }
  }

  private generateAudioWaveBars(seed: string, size = 56) {
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) {
      hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
    }
    let value = Math.abs(hash) + 1;
    const bars: number[] = [];
    for (let i = 0; i < size; i += 1) {
      value = (value * 1664525 + 1013904223) >>> 0;
      const noise = value % 14;
      const wave = Math.sin((i / Math.max(1, size - 1)) * Math.PI) * 12;
      bars.push(6 + noise + Math.round(wave));
    }
    return bars;
  }

  private audioPreviewLabel(duration?: number | null) {
    if (!duration) return 'Áudio';
    return `Áudio · ${this.formatRecordingTime(duration)}`;
  }

  openImageModal(src?: string | null) {
    if (!src) return;
    this.imageModalSrc = src;
  }

  closeImageModal() {
    this.imageModalSrc = null;
  }

  submitGroup() {
    if (!this.tenantId || !this.userId || !this.canCreate) return;
    const name = this.groupName.trim();
    if (!name || this.groupMembers.length === 0) return;
    const members = this.groupMembers.map((member) => member.id);
    this.chatService.createGroupConversation(this.tenantId, this.userId, name, members).subscribe({
      next: (response) => {
        const convId = Number(response?.conversation_id || 0);
        if (convId && !this.conversations.some((conv) => conv.id === convId)) {
          const now = new Date().toISOString();
          const tempConversation: ChatConversation = {
            id: convId,
            type: 'GROUP',
            group_name: name,
            group_created_by: this.userId || null,
            members_count: members.length + 1,
            last_activity_at: now,
            last_message_at: null,
            last_message_text: null,
            last_message_file_name: null,
            last_message_file_path: null,
            group_only_admins: 0
          };
          this.conversations = [tempConversation, ...this.conversations];
        }
        this.loadConversations();
        this.isCreatingGroup = false;
        this.resetGroupForm();
        this.snackBar.open('Grupo criado com sucesso.', 'OK', { duration: 2500 });
        if (this.isHeaderMenuOpen) {
          this.headerMenuView = 'root';
        }
      }
    });
  }

  toggleGroupSettings() {
    this.showGroupSettings = !this.showGroupSettings;
  }

  onGroupOnlyAdminsChange(checked: boolean) {
    if (!this.tenantId || !this.userId || !this.selectedConversation || this.selectedConversation.type !== 'GROUP') {
      return;
    }
    if (!this.isGroupAdminOrOwner()) {
      this.groupOnlyAdmins = !!this.selectedConversation.group_only_admins;
      return;
    }
    this.chatService
      .updateGroupSettings(this.tenantId, this.userId, this.selectedConversation.id, { only_admins: checked ? 1 : 0 })
      .subscribe({
        next: () => {
          this.groupOnlyAdmins = checked;
          this.selectedConversation = {
            ...this.selectedConversation!,
            group_only_admins: checked ? 1 : 0
          };
          this.loadConversations();
        },
        error: () => {
          this.groupOnlyAdmins = !!this.selectedConversation?.group_only_admins;
        }
      });
  }

  private resetGroupForm() {
    this.groupName = '';
    this.groupMemberQuery = '';
    this.groupMemberResults = [];
    this.groupMembers = [];
  }

  loadKeywords() {
    if (!this.tenantId || !this.userId) return;
    this.loadingKeywords = true;
    this.chatService.getKeywords(this.tenantId, this.userId).subscribe({
      next: (rows) => {
        this.keywords = rows || [];
        this.loadingKeywords = false;
      },
      error: () => {
        this.loadingKeywords = false;
      }
    });
  }

  addKeyword() {
    if (!this.keywordInput.trim() || !this.tenantId || !this.userId) return;
    this.chatService.createKeyword(this.tenantId, this.userId, this.keywordInput.trim()).subscribe({
      next: (row) => {
        this.keywords = [row, ...this.keywords];
        this.keywordInput = '';
      }
    });
  }

  removeKeyword(keywordId: number) {
    if (!this.tenantId || !this.userId) return;
    this.chatService.deleteKeyword(this.tenantId, this.userId, keywordId).subscribe({
      next: () => {
        this.keywords = this.keywords.filter((item) => item.id !== keywordId);
      }
    });
  }

  loadMonitoring() {
    if (!this.tenantId || !this.userId) return;
    this.loadingMonitoring = true;
    this.chatService.getMonitoring(this.tenantId, this.userId).subscribe({
      next: (rows) => {
        this.monitoringHits = rows || [];
        this.loadingMonitoring = false;
      },
      error: () => {
        this.loadingMonitoring = false;
      }
    });
  }
}
